import type { NavigateFunction } from 'react-router';
import { ROUTES } from '@/shared/config/routes';
import type { RootNavigationState } from '@/types/routing.types';
import { getAgentMetadataHelpers } from './agent-metadata-bindings';

/**
 * Launch an agent with its auto-open widget in a new chat session.
 * This ensures consistent behavior across all entry points (dashboard, springboard, most used tools, etc.)
 *
 * @param navigate - React Router navigate function
 * @param agentId - ID of the agent to launch
 * @param inputs - Optional inputs to pass to the agent
 * @param customWidgetData - Optional custom widget data to override/merge with default widget data
 */
export function launchAgentWithWidget(
  navigate: NavigateFunction,
  agentId: string,
  inputs?: Record<string, unknown>,
  customWidgetData?: Record<string, unknown>,
): void {
  const { autoOpen, layoutMode, data } = getAgentMetadataHelpers().getInitialWidgetState(agentId);

  if (autoOpen && data) {
    // Agent has auto-open widget - navigate with widgetData like dashboard does
    const baseData = typeof data.data === 'object' && data.data !== null ? data.data : {};
    void navigate(ROUTES.NEW_CHAT_SESSION, {
      state: {
        agentId,
        inputs,
        widgetData: {
          key: data.key,
          data: customWidgetData ? { ...baseData, ...customWidgetData } : data.data,
          fullWidth: layoutMode === 'fullscreen',
        },
      } satisfies RootNavigationState,
    });
  } else {
    // Agent doesn't have auto-open widget - navigate with just agentId
    void navigate(ROUTES.NEW_CHAT_SESSION, {
      state: {
        agentId,
        inputs,
      } satisfies RootNavigationState,
    });
  }
}
